/**
 * The field types whose whole configuration is one control.
 *
 * Together rather than in a file each: six components of a dozen lines, none of
 * which has anything to say that its own module would carry.
 */
import { useId } from 'react'
import { useTranslation } from 'react-i18next'

import { Input } from '@/shared/ui/input'
import { NativeSelect, NativeSelectOption } from '@/shared/ui/native-select'
import {
  DEFAULT_RATING_STYLE,
  RATING_STYLES,
  ratingColorValue,
  type RatingStyle,
} from '../../rating'
import { iconForStyle } from '../rating'
import { ColourPicker, Setting, Toggle } from './controls'
import type { SubForm } from './types'

/** What a new row starts with when nobody has typed anything into the cell. */
export const TextForm: SubForm<'text'> = ({ properties, onChange }) => {
  const { t } = useTranslation()
  const id = useId()

  return (
    <Setting id={id} label={t('fieldConfig.defaultValue')}>
      <Input
        id={id}
        value={properties.text_default ?? ''}
        placeholder={t('fieldConfig.defaultTextPlaceholder')}
        onChange={(event) => {
          onChange({ text_default: event.target.value })
        }}
      />
    </Setting>
  )
}

/**
 * Rich text turns the cell from a string into a document.
 *
 * Which is why a rich text field cannot be the primary one, and why the dialog
 * has to re-check that whenever this is switched on.
 */
export const LongTextForm: SubForm<'long_text'> = ({ properties, onChange, field }) => {
  const { t } = useTranslation()
  const id = useId()

  // Not offered on the primary field at all, rather than offered and refused:
  // the backend will not have a document as the one line that stands for a row,
  // and the type picker has already made the same decision the other way round.
  if (field.primary === true) return null

  return (
    <Toggle
      id={id}
      label={t('fieldConfig.richText')}
      hint={t('fieldConfig.richTextHint')}
      checked={properties.long_text_enable_rich_text === true}
      onChange={(long_text_enable_rich_text) => {
        onChange({ long_text_enable_rich_text })
      }}
    />
  )
}

export const BooleanForm: SubForm<'boolean'> = ({ properties, onChange }) => {
  const { t } = useTranslation()
  const id = useId()

  return (
    <Toggle
      id={id}
      label={t('fieldConfig.defaultTicked')}
      checked={properties.boolean_default === true}
      onChange={(boolean_default) => {
        onChange({ boolean_default })
      }}
    />
  )
}

/** The backend's own ceiling, which is also what the cell will draw up to. */
const RATING_MAXIMUMS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const

export const RatingForm: SubForm<'rating'> = ({ properties, onChange }) => {
  const { t } = useTranslation()
  const id = useId()
  const styleId = useId()
  const style = properties.style ?? DEFAULT_RATING_STYLE
  const Symbol = iconForStyle(style)

  return (
    <>
      <Setting id={id} label={t('fieldConfig.ratingMax')}>
        <NativeSelect
          id={id}
          className="w-full"
          value={String(properties.max_value ?? 5)}
          onChange={(event) => {
            onChange({ max_value: Number(event.target.value) })
          }}
        >
          {RATING_MAXIMUMS.map((max) => (
            <NativeSelectOption key={max} value={max}>
              {max}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </Setting>

      <Setting id={styleId} label={t('fieldConfig.ratingSymbol')}>
        <div className="flex items-center gap-2">
          <NativeSelect
            id={styleId}
            className="flex-1"
            value={style}
            onChange={(event) => {
              onChange({ style: event.target.value as RatingStyle })
            }}
          >
            {RATING_STYLES.map((name) => (
              <NativeSelectOption key={name} value={name}>
                {t(`fieldConfig.ratingStyle.${name}`)}
              </NativeSelectOption>
            ))}
          </NativeSelect>

          <ColourPicker
            label={t('fieldConfig.ratingColour')}
            colour={properties.color}
            onChange={(color) => {
              onChange({ color })
            }}
          />

          {/* The two choices, together, as the cell will draw them. Shown rather
              than described: a name and a swatch say what was picked, and this
              says what it looks like. */}
          <Symbol
            aria-hidden
            className="size-5 shrink-0"
            style={{
              color: ratingColorValue(properties.color),
              fill: 'currentColor',
            }}
          />
        </div>
      </Setting>
    </>
  )
}

export const CollaboratorsForm: SubForm<'multiple_collaborators'> = ({
  properties,
  onChange,
}) => {
  const { t } = useTranslation()
  const id = useId()

  return (
    <Toggle
      id={id}
      label={t('fieldConfig.notifyCollaborator')}
      hint={t('fieldConfig.notifyCollaboratorHint')}
      checked={properties.notify_user_when_added === true}
      onChange={(notify_user_when_added) => {
        onChange({ notify_user_when_added })
      }}
    />
  )
}

/**
 * A password is written and never read back.
 *
 * The one thing that can be done with a stored password is ask whether a given
 * one matches, and the switch below is what opens the endpoint that answers.
 * Upstream describes it as the way to authenticate against the application
 * builder, which this fork does not have; what is left is the endpoint itself.
 */
export const PasswordForm: SubForm<'password'> = ({ properties, onChange }) => {
  const { t } = useTranslation()
  const id = useId()

  return (
    <Toggle
      id={id}
      label={t('fieldConfig.passwordEndpoint')}
      hint={t('fieldConfig.passwordEndpointHint')}
      checked={properties.allow_endpoint_authentication === true}
      onChange={(allow_endpoint_authentication) => {
        onChange({ allow_endpoint_authentication })
      }}
    />
  )
}
